import { useMemo, useState } from "react";
import type { Contact } from "types";
import type { TableColumn } from "common/components/DataTable/DataTable";
import CrudList from "common/components/Crud/CrudList";
import useItem from "hooks/useItem";
import useAuth from "hooks/useAuth";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";
import type { CrudFilter } from "common/components/Crud/components/CrudFilters";
import { Dropdown } from "primereact/dropdown";

// ─── Labels ───────────────────────────────────────────────────────────────────

const SOURCE_LABELS: Record<string, { label: string; color: string }> = {
  missing_zone: { label: "Rizière manquante", color: "bg-amber-100 text-amber-700" },
  app_question: { label: "Question application", color: "bg-blue-100 text-blue-700" },
};

// ─── IsDone Toggle ────────────────────────────────────────────────────────────

function IsDoneToggle({ id, value, readonly }: { id: number; value: boolean; readonly?: boolean }) {
  const { updateItem } = useItem<Contact>("contacts", null);
  const [isDone, setIsDone] = useState(value);

  const handleToggle = async (e: React.MouseEvent) => {
    if (readonly) return;
    e.stopPropagation();
    const newValue = !isDone;
    setIsDone(newValue);
    await updateItem(id, { isDone: newValue });
  };

  return (
    <button
      type="button"
      onClick={handleToggle}
      disabled={readonly}
      className="flex items-center gap-1 px-2 py-1 rounded-lg text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-default"
      style={{ color: isDone ? "#16a34a" : "#94a3b8" }}
    >
      {isDone ? (
        <CheckCircleIcon className="w-5 h-5" />
      ) : (
        <XCircleIcon className="w-5 h-5" />
      )}
      {isDone ? "Traité" : "À traiter"}
    </button>
  );
}

// ─── Source Badge ─────────────────────────────────────────────────────────────

function SourceBadge({ source }: { source: string | null }) {
  if (!source) return <span className="text-slate-400">—</span>;
  const { label, color } = SOURCE_LABELS[source] ?? { label: source, color: "bg-slate-100 text-slate-600" };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      {label}
    </span>
  );
}

// ─── Source Filter ────────────────────────────────────────────────────────────

const SOURCE_OPTIONS = [
  { label: "Rizière manquante", value: "missing_zone" },
  { label: "Question application", value: "app_question" },
];

function SourceDropdown({ value, onChange, className }: { value?: string; onChange?: (v: string) => void; className?: string }) {
  return (
    <Dropdown
      inputId="source$eq"
      value={value ?? null}
      options={SOURCE_OPTIONS}
      onChange={(e) => onChange?.(e.value ?? "")}
      placeholder="Tous les types"
      showClear
      className={className}
    />
  );
}

// ─── Contacts Page ────────────────────────────────────────────────────────────

function Contacts() {
  const { user: currentUser } = useAuth();
  const isReadonly = currentUser?.isMonitoring;

  const columns: TableColumn<Contact>[] = useMemo(
    () => [
      {
        field: "name",
        header: "Nom",
        sortable: true,
        renderType: "bold",
      },
      {
        field: "source",
        header: "Type",
        customRender: (rowData: Contact) => <SourceBadge source={rowData.source} />,
      },
      {
        field: "phone",
        header: "Téléphone",
      },
      {
        field: "address",
        header: "Adresse",
        customRender: (rowData: Contact) => rowData.address ?? "-",
      },
      {
        field: "description",
        header: "Informations",
        customRender: (rowData: Contact) => rowData.description ?? "-",
      },
      {
        field: "createdAt",
        header: "Date",
        sortable: true,
        customRender: (rowData: Contact) =>
          rowData.createdAt
            ? new Date(rowData.createdAt).toLocaleDateString("fr-FR")
            : "-",
      },
      {
        field: "isDone",
        header: "Statut",
        sortable: true,
        customRender: (rowData: Contact) => (
          <IsDoneToggle id={rowData.id} value={rowData.isDone ?? false} readonly={isReadonly} />
        ),
      },
    ],
    [isReadonly]
  );

  const filters: CrudFilter[] = useMemo(
    () => [
      {
        name: "source$eq",
        label: "Type",
        renderType: "custom",
        renderComponent: <SourceDropdown />,
      },
    ],
    []
  );

  return (
    <CrudList
      collectionName="contacts"
      columns={columns}
      filters={filters}
      defaultSort="createdAt:DESC"
      canEdit={false}
      canDelete={false}
      canSeeDetails={false}
      icon="EnvelopeIcon"
      actionsHeader=""
    />
  );
}

export default Contacts;
